import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Grid,
  Button,
} from "@mui/material";
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  FilterList,
  Clear,
  Add as AddIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../auth";
import ListLayout from "../../../../components/Layout/ListLayout";
import {
  getDeleteGroupMutationOptions,
  getDisableGroupMutationOptions,
  getListGroupQueryOptions,
  getEnableGroupMutationOptions,
} from "../../../../api/bpm/endpoints";
import { SmartGroupDto } from "../../../../api/bpm/model";
import { FormTextField } from "../../../../forms/FormTextField";
import { GroupCards } from "./components/GroupCards";
import { ConfirmModal } from "../../../../components/ConfirmModal";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { LogsModal } from "../../../../components/LogsModal";
import { useTranslation } from "react-i18next";

const groupsSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
  enabled: z.enum(["true", "false"]).optional().catch(undefined),
});

export const Route = createFileRoute("/__auth/admin/groups/")({
  component: AdminGroupsPage,
  validateSearch: (search) => groupsSearchSchema.parse(search),
  staticData: {
    title: "routes.groups",
  },
});

function GroupActionsCell({ row }: { row: SmartGroupDto }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const tenant = user?.tenant || "default-domain";

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const enableMutation = useMutation({
    ...getEnableGroupMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['listGroup'],
      });
      setIsConfirmOpen(false);
    },
  });

  const disableMutation = useMutation({
    ...getDisableGroupMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['listGroup'],
      });
      setIsConfirmOpen(false);
    },
  });

  const deleteMutation = useMutation({
    ...getDeleteGroupMutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['listGroup'],
      });
      setIsDeleteConfirmOpen(false);
    },
  });

  const handleToggleStatus = () => {
    if (row.enabled) {
      disableMutation.mutate({
        groupId: row.id!,
        tenant,
      });
    } else {
      enableMutation.mutate({
        groupId: row.id!,
        tenant,
      });
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate({
      groupId: row.id!,
      tenant,
    });
  };

  const isStatusLoading = enableMutation.isPending || disableMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            navigate({ to: `/admin/groups/${row.id}` });
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common.view")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            navigate({ to: `/admin/groups/${row.id}/edit` });
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common.edit")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setIsConfirmOpen(true);
          }}
        >
          <ListItemIcon>
            {row.enabled ? (
              <BlockIcon fontSize="small" color="error" />
            ) : (
              <CheckCircleIcon fontSize="small" color="success" />
            )}
          </ListItemIcon>
          <ListItemText>{row.enabled ? t("common.deactivate") : t("common.activate")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setIsLogsModalOpen(true);
          }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setIsDeleteConfirmOpen(true);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common.delete")}</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmModal
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleToggleStatus}
        title={row.enabled ? t("admin.groups.deactivateTitle") : t("admin.groups.activateTitle")}
        description={row.enabled ? t("admin.groups.deactivateConfirmation", { name: row.name }) : t("admin.groups.activateConfirmation", { name: row.name })}
        confirmText={row.enabled ? t("common.deactivate") : t("common.activate")}
        confirmColor={row.enabled ? "error" : "primary"}
        isLoading={isStatusLoading}
      />

      <ConfirmModal
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title={t("admin.groups.deleteTitle")}
        description={t("admin.groups.deleteConfirmation", { name: row.name })}
        confirmText={t("common.delete")}
        confirmColor="error"
        isLoading={isDeleteLoading}
      />

      <LogsModal
        open={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
        entityId={row.id || ""}
        entityName={row.name || ""}
        tenant={tenant}
        entityType="groups"
      />
    </>
  );
}

const filterSchema = z.object({
  q: z.string().optional(),
  enabled: z.string().optional(),
});

type GroupFiltersData = z.infer<typeof filterSchema>;

interface GroupFiltersProps {
  onFilter: (data: GroupFiltersData) => void;
  onClear: () => void;
  isLoading?: boolean;
}

function GroupFilters({ onFilter, onClear, isLoading }: GroupFiltersProps) {
  const { t } = useTranslation();
  const methods = useForm<GroupFiltersData>({
    resolver: zodResolver(filterSchema),
    defaultValues: {
      q: "",
      enabled: "all",
    },
  });

  const { handleSubmit, reset } = methods;

  const handleClear = () => {
    reset({ q: "", enabled: "all" });
    onClear();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFilter)}>
        <Grid
          container
          spacing={2}
          sx={{ mb: 3, p: 2, bgcolor: "background.paper", borderRadius: 1 }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <FormTextField
              name="q"
              label={t("admin.common.nameOrDescription")}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <FormTextField
              name="enabled"
              label={t("admin.common.situation")}
              size="small"
              fullWidth
              select
            >
              <MenuItem value="all">{t("common.all")}</MenuItem>
              <MenuItem value="true">{t("common.active")}</MenuItem>
              <MenuItem value="false">{t("common.inactive")}</MenuItem>
            </FormTextField>
          </Grid>

          <Grid size={12}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="outlined"
                onClick={handleClear}
                startIcon={<Clear />}
              >{t("common.clear")}</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                startIcon={<FilterList />}
              >{t("common.filter")}</Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
}

function AdminGroupsPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const tenant = user?.tenant || "default-domain";

  const enabledFilter =
    search.enabled === "true"
      ? true
      : search.enabled === "false"
        ? false
        : undefined;

  const { data, isLoading, error } = useQuery(
    getListGroupQueryOptions(tenant, {
            page: search.page - 1,
            size: search.size,
            q: search.q,
            enabled: enabledFilter
        } as any),
  );

  const handleFilter = (filters: GroupFiltersData) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...filters,
        enabled:
          filters.enabled === "all"
            ? undefined
            : (filters.enabled as "true" | "false" | undefined),
        page: 1,
      }),
    });
  };

  const handleClearFilters = () => {
    navigate({
      search: (prev) => ({
        page: 1,
        size: prev.size,
      }),
    });
  };

  const columnHelper = createColumnHelper<SmartGroupDto>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t("common.name"),
        cell: (info) => (
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {info.getValue()}
            </Typography>
          </Box>
        ),
      }),
      columnHelper.accessor("description", {
        header: t("admin.common.description"),
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("creator", {
        header: t("admin.common.createdBy"),
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("enabled", {
        header: t("admin.common.situation"),
        cell: (info) => (
          <Chip
            label={info.getValue() ? t("common.active") : t("common.inactive")}
            color={info.getValue() ? "success" : "default"}
            size="small"
            variant="outlined"
          />
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: t("common.actions"),
        cell: (info) => <GroupActionsCell row={info.row.original} />,
        meta: { align: "right", isSticky: true },
      }),
    ],
    [columnHelper],
  );

  const table = useReactTable({
    data: (data as any)?.content || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: (data as any)?.totalPages || -1,
  });

  return (
    <ListLayout
      title={t("admin.groups.title")}
      subtitle={t("admin.groups.subtitle")}
      isLoading={isLoading}
      error={error}
      isEmpty={!(data as any)?.content?.length}
      filters={
        <GroupFilters
          onFilter={handleFilter}
          onClear={handleClearFilters}
          isLoading={isLoading}
        />
      }
      actions={
        <IconButton
          onClick={() => navigate({ to: "/admin/groups/create" })}
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { bgcolor: "primary.dark" },
            boxShadow: 2,
          }}
        >
          <AddIcon />
        </IconButton>
      }
    >
      {isMobile ? (
        <GroupCards groups={(data as any)?.content || []} />
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider" }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: "background.default" }}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const meta: any = header.column.columnDef.meta || {};
                    return (
                      <TableCell
                        key={header.id}
                        sx={{
                          fontWeight: 600,
                          ...(meta.isSticky && {
                            position: "sticky",
                            right: 0,
                            bgcolor: "background.default",
                            zIndex: 1,
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.02)",
                          }),
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    bgcolor: "background.paper",
                    "&:hover": { bgcolor: "grey.50" },
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta: any = cell.column.columnDef.meta || {};
                    return (
                      <TableCell
                        key={cell.id}
                        sx={{
                          ...(meta.isSticky && {
                            position: "sticky",
                            right: 0,
                            bgcolor: "inherit",
                            zIndex: 1,
                            boxShadow: "-2px 0 4px rgba(0,0,0,0.02)",
                          }),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ListLayout>
  );
}
